import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import type { VisibilityRule } from '../lease-details.config';

// Evaluates `VisibilityRule` against live form state. To avoid re-rendering on
// every keystroke, `useVisibilityRule` subscribes only to the fields the rule
// reads (collected by `collectWatchedFields`), never to the whole form.

const isVisibilityRule = (value: unknown): value is VisibilityRule => {
  if (typeof value !== 'object' || value === null) return false;
  return 'whenField' in value || 'all' in value || 'any' in value || 'not' in value;
};

/**
 * Walks a rule and returns every form-field path it references via `whenField`.
 * The returned list is the minimal set of field paths the rule depends on, and
 * is passed directly to RHF's `useWatch({ name: [...] })`.
 *
 * This is the perf win: a rule that reads two fields only triggers a re-render
 * when those two fields change, not on any change anywhere in the form.
 *
 * @example
 *   collectWatchedFields({ whenField: 'screening_result', equals: 'Custom' });
 *   // → ['screening_result']
 *
 *   collectWatchedFields({
 *     all: [
 *       { whenField: 'a', equals: 1 },
 *       { whenField: 'b', in: [2, 3] },
 *       { not: { whenField: 'a', equals: 0 } },  // duplicates are deduped
 *     ],
 *   });
 *   // → ['a', 'b']
 */
export const collectWatchedFields = (rule: VisibilityRule): string[] => {
  const paths = new Set<string>();
  const visit = (node: VisibilityRule) => {
    if ('whenField' in node) {
      paths.add(node.whenField);
      return;
    }
    if ('all' in node) {
      node.all.forEach(visit);
      return;
    }
    if ('any' in node) {
      node.any.forEach(visit);
      return;
    }
    if ('not' in node) {
      visit(node.not);
    }
  };
  visit(rule);
  return Array.from(paths);
};

// Pure evaluator — exported for tests and for any caller that wants to check
// a rule against an arbitrary value map without subscribing to RHF.
export const evaluateVisibilityRule = (
  rule: VisibilityRule,
  values: Record<string, unknown>,
): boolean => {
  if ('whenField' in rule) {
    const fieldValue = values[rule.whenField];
    if ('equals' in rule) {
      return fieldValue === rule.equals;
    }
    return rule.in.includes(fieldValue);
  }
  if ('all' in rule) {
    return rule.all.every((sub) => evaluateVisibilityRule(sub, values));
  }
  if ('any' in rule) {
    return rule.any.some((sub) => evaluateVisibilityRule(sub, values));
  }
  return !evaluateVisibilityRule(rule.not, values);
};

/**
 * Evaluates a `VisibilityRule` against live RHF form state. Pass-through for
 * `boolean`; subscribes to the rule's minimal watched-field set otherwise.
 * Returns `false` for `undefined` or malformed rules.
 */
export const useVisibilityRule = (rule: boolean | VisibilityRule | undefined): boolean => {
  const isBoolean = typeof rule === 'boolean' || rule === undefined;
  // Memoize the watched-field list so we only re-walk the rule tree when the
  // rule reference itself changes. Consumers must keep rule references stable
  // across renders (see the `VisibilityRule` docstring) for this to be useful.
  const watchedFields = useMemo(
    () => (isBoolean ? [] : collectWatchedFields(rule)),
    [isBoolean, rule],
  );
  const watched = useWatch({ name: watchedFields }) as unknown[];

  if (typeof rule === 'boolean') return rule;
  if (rule === undefined) return false;
  if (!isVisibilityRule(rule)) return false;
  // `watched` is a fresh array each render even when values are unchanged, so
  // memoizing this small map would be a false economy — inline.
  const valueMap: Record<string, unknown> = {};
  watchedFields.forEach((path, index) => {
    valueMap[path] = watched[index];
  });
  return evaluateVisibilityRule(rule, valueMap);
};
